/**
 * CoordinateCalculator - Centralized coordinate calculation logic
 * Following DRY principle - single source of truth for coordinate calculations
 */

import { devWarn } from '../../../../../utils/devLog';
import { DEGREE_DIVISIONS_CONFIG } from '../config/PlanetLayerConfig';


/**
 * Container bounds interface
 */
export interface ContainerBounds {
    width: number;
    height: number;
    x?: number;  // Optional absolute x position for overlay rendering
    y?: number;  // Optional absolute y position for overlay rendering
    index?: number;  // Container index for calculations
}

/**
 * Line coordinates interface
 */
export interface LineCoordinates {
    startX: number;
    startY: number;
    endX: number;
    endY: number;
}

/**
 * Point coordinates interface
 */
export interface Point {
    x: number;
    y: number;
}

/**
 * Degree division mark interface
 */
export interface DegreeDivisionMark {
    startPoint: Point;
    endPoint: Point;
    degree: number;
    isMajor: boolean; // true for 5-degree marks
}

/**
 * CoordinateCalculator - Centralized coordinate calculation (DRY principle)
 */
export class CoordinateCalculator {
    private static instance: CoordinateCalculator;
    private debugMode: boolean = false;

    private constructor(debugMode: boolean = false) {
        this.debugMode = debugMode;
    }

    /**
     * Get singleton instance
     */
    public static getInstance(debugMode: boolean = false): CoordinateCalculator {
        if (!CoordinateCalculator.instance) {
            CoordinateCalculator.instance = new CoordinateCalculator(debugMode);
        }
        return CoordinateCalculator.instance;
    }

    /**
     * Calculate degree line coordinates for container (DRY - single implementation)
     */
    public calculateDegreeLineCoordinates(containerIndex: number, containerBounds: ContainerBounds, insetRatio: number = 0): LineCoordinates {
        let result: LineCoordinates;
        
        // Calculate inset offset from container size
        // insetRatio: 0 = inner boundary, 1 = outer boundary
        const insetOffset = containerBounds.width * insetRatio;
        
        switch(containerIndex) {
            case 0: {
                // Container 0 = House 12 (left-top corner): connects from left vertical to top horizontal
                const start = { x: insetOffset, y: containerBounds.height }; // left edge start
                const end = { x: containerBounds.width, y: insetOffset }; // top edge end
                result = { startX: start.x, startY: start.y, endX: end.x, endY: end.y };
                break;
            }
            case 1: {
                // House 1 (top horizontal): 0=inner edge, 1=outer edge
                const yPos = insetOffset; // 0 = y:0 (top), 1 = y:196 (bottom)
                const start = { x: 0, y: yPos };
                const end = { x: containerBounds.width, y: yPos };
                result = { startX: start.x, startY: start.y, endX: end.x, endY: end.y };
                break;
            }
            case 2: {
                // House 2 (top horizontal): 0=inner edge, 1=outer edge
                const yPos = insetOffset; // 0 = y:0 (top), 1 = y:196 (bottom)
                const start = { x: 0, y: yPos };
                const end = { x: containerBounds.width, y: yPos };
                result = { startX: start.x, startY: start.y, endX: end.x, endY: end.y };
                break;
            }
            case 3: {
                // House 3 (right-top corner): connects top edge to right edge
                const start = { x: 0, y: insetOffset };
                const end = { x: containerBounds.width - insetOffset, y: containerBounds.height };
                result = { startX: start.x, startY: start.y, endX: end.x, endY: end.y };
                break;
            }
            case 4: {
                // House 4 (right vertical): 0=inner edge, 1=outer edge
                const xPos = containerBounds.width - insetOffset; // 0 = x:196 (right), 1 = x:0 (left)
                const start = { x: xPos, y: 0 };
                const end = { x: xPos, y: containerBounds.height };
                result = { startX: start.x, startY: start.y, endX: end.x, endY: end.y };
                break;
            }
            case 5: {
                // House 5 (right vertical): 0=inner edge, 1=outer edge
                const xPos = containerBounds.width - insetOffset; // 0 = x:196 (right), 1 = x:0 (left)
                const start = { x: xPos, y: 0 };
                const end = { x: xPos, y: containerBounds.height };
                result = { startX: start.x, startY: start.y, endX: end.x, endY: end.y };
                break;
            }
            case 6: {
                // House 6 (right-bottom corner): connects right edge to bottom edge
                const start = { x: containerBounds.width - insetOffset, y: 0 };
                const end = { x: 0, y: containerBounds.height - insetOffset };
                result = { startX: start.x, startY: start.y, endX: end.x, endY: end.y };
                break;
            }
            case 7: {
                // House 7 (bottom horizontal): 0=inner edge, 1=outer edge
                const yPos = containerBounds.height - insetOffset; // 0 = y:196 (bottom), 1 = y:0 (top)
                const start = { x: containerBounds.width, y: yPos };
                const end = { x: 0, y: yPos };
                result = { startX: start.x, startY: start.y, endX: end.x, endY: end.y };
                break;
            }
            case 8: {
                // House 8 (bottom horizontal): 0=inner edge, 1=outer edge
                const yPos = containerBounds.height - insetOffset; // 0 = y:196 (bottom), 1 = y:0 (top)
                const start = { x: containerBounds.width, y: yPos };
                const end = { x: 0, y: yPos };
                result = { startX: start.x, startY: start.y, endX: end.x, endY: end.y };
                break;
            }
            case 9: {
                // House 9 (left-bottom corner): connects bottom edge to left edge
                const start = { x: containerBounds.width, y: containerBounds.height - insetOffset };
                const end = { x: insetOffset, y: 0 };
                result = { startX: start.x, startY: start.y, endX: end.x, endY: end.y };
                break;
            }
            case 10: {
                // House 10 (left vertical): 0=inner edge, 1=outer edge
                const xPos = insetOffset; // 0 = x:0 (left), 1 = x:196 (right)
                const start = { x: xPos, y: containerBounds.height };
                const end = { x: xPos, y: 0 };
                result = { startX: start.x, startY: start.y, endX: end.x, endY: end.y };
                break;
            }
            case 11: {
                // House 11 (left vertical): 0=inner edge, 1=outer edge
                const xPos = insetOffset; // 0 = x:0 (left), 1 = x:196 (right)
                const start = { x: xPos, y: containerBounds.height };
                const end = { x: xPos, y: 0 };
                result = { startX: start.x, startY: start.y, endX: end.x, endY: end.y };
                break;
            }
            default: {
                const start = { x: 0, y: containerBounds.height / 2 };
                const end = { x: containerBounds.width, y: containerBounds.height / 2 };
                result = { startX: start.x, startY: start.y, endX: end.x, endY: end.y };
                break;
            }
        }


        return result;
    }

    /**
     * Create corner arc path data (DRY - centralized path generation)
     */
    public createCornerArcPath(containerIndex: number, lineCoords: LineCoordinates, containerBounds: ContainerBounds, insetRatio: number = 0): string {
        const insetOffset = containerBounds.width * insetRatio;
        
        // Радиус УМЕНЬШАЕТСЯ с inset: при inset=0 максимальный радиус, при inset=1 минимальный радиус
        const minRadius = 10; // минимальный радиус в центре
        const maxRadius = Math.min(containerBounds.width, containerBounds.height) * 0.4; // максимальный радиус на краю
        const cornerRadius = maxRadius - (maxRadius - minRadius) * insetRatio;
        
        switch(containerIndex) {
            case 0: // House 12: left edge → arc → top edge (with inset)
                return `M ${lineCoords.startX} ${lineCoords.startY} L ${insetOffset} ${cornerRadius + insetOffset} A ${cornerRadius} ${cornerRadius} 0 0 1 ${cornerRadius + insetOffset} ${insetOffset} L ${lineCoords.endX} ${lineCoords.endY}`;
            case 3: // House 3: top edge → arc → right edge (with inset)
                return `M ${lineCoords.startX} ${lineCoords.startY} L ${containerBounds.width - cornerRadius - insetOffset} ${insetOffset} A ${cornerRadius} ${cornerRadius} 0 0 1 ${containerBounds.width - insetOffset} ${cornerRadius + insetOffset} L ${lineCoords.endX} ${lineCoords.endY}`;
            case 6: // House 6: right edge → arc → bottom edge (with inset)
                return `M ${lineCoords.startX} ${lineCoords.startY} L ${containerBounds.width - insetOffset} ${containerBounds.height - cornerRadius - insetOffset} A ${cornerRadius} ${cornerRadius} 0 0 1 ${containerBounds.width - cornerRadius - insetOffset} ${containerBounds.height - insetOffset} L ${lineCoords.endX} ${lineCoords.endY}`;
            case 9: // House 9: bottom edge → arc → left edge (with inset)
                return `M ${lineCoords.startX} ${lineCoords.startY} L ${cornerRadius + insetOffset} ${containerBounds.height - insetOffset} A ${cornerRadius} ${cornerRadius} 0 0 1 ${insetOffset} ${containerBounds.height - cornerRadius - insetOffset} L ${lineCoords.endX} ${lineCoords.endY}`;
            default:
                // Fallback to simple arc
                return `M ${lineCoords.startX} ${lineCoords.startY} A ${cornerRadius} ${cornerRadius} 0 0 1 ${lineCoords.endX} ${lineCoords.endY}`;
        }
    }

    /**
     * Get point on arc path using SVG path calculation (DRY - centralized arc logic)
     * Uses the same path as createCornerArcPath to ensure consistency
     */
    public getPointOnArcPath(containerIndex: number, progress: number, containerBounds: ContainerBounds, insetRatio: number = 0): Point {
        // Get the exact same path as used in createCornerArcPath
        const lineCoords = this.calculateDegreeLineCoordinates(containerIndex, containerBounds, insetRatio);
        const pathData = this.createCornerArcPath(containerIndex, lineCoords, containerBounds, insetRatio);
        
        // Create temporary SVG path to get point at length
        // NOTE: This DOM creation is necessary for SVG path calculations
        const tempPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        tempPath.setAttribute('d', pathData);
        
        // Get total length and calculate point at progress
        const totalLength = tempPath.getTotalLength();
        const targetLength = totalLength * progress;
        const point = tempPath.getPointAtLength(targetLength);
        
        return {x: point.x, y: point.y};
    }

    /**
     * Check if house is corner house (DRY - single definition)
     * Corner containers according to DEGREE_LINE_RENDERING_ALGORITHM.md: 0, 3, 6, 9
     */
    public static isCornerHouse(containerIndex: number): boolean {
        return [0, 3, 6, 9].includes(containerIndex);
    }

    /**
     * Calculate degree division marks for a line (30 divisions = 30 degrees)
     */
    public calculateDegreeDivisions(
        containerIndex: number, 
        containerBounds: ContainerBounds, 
        insetRatio: number = 0
    ): DegreeDivisionMark[] {
        // const divisions: DegreeDivisionMark[] = [];
        const isCornerHouse = CoordinateCalculator.isCornerHouse(containerIndex);
        
        if (isCornerHouse) {
            // For corner houses, calculate divisions along the arc
            return this.calculateArcDegreeDivisions(containerIndex, containerBounds, insetRatio);
        } else {
            // For straight houses, calculate divisions along the line
            return this.calculateLineDivisions(containerIndex, containerBounds, insetRatio);
        }
    }

    /**
     * Calculate divisions for straight lines (houses 1, 2, 4, 5, 7, 8, 10, 11)
     */
    private calculateLineDivisions(
        containerIndex: number, 
        containerBounds: ContainerBounds, 
        insetRatio: number = 0
    ): DegreeDivisionMark[] {
        const divisions: DegreeDivisionMark[] = [];
        const lineCoords = this.calculateDegreeLineCoordinates(containerIndex, containerBounds, insetRatio);
        
        // Calculate perpendicular direction for division marks
        const lineLength = Math.sqrt(
            Math.pow(lineCoords.endX - lineCoords.startX, 2) + 
            Math.pow(lineCoords.endY - lineCoords.startY, 2)
        );
        
        // Unit vector along the line
        const unitX = (lineCoords.endX - lineCoords.startX) / lineLength;
        const unitY = (lineCoords.endY - lineCoords.startY) / lineLength;
        
        // Perpendicular unit vector (90 degrees rotated)
        const perpX = -unitY;
        const perpY = unitX;
        
        // Create divisions based on spacing config
        const totalDivisions = 30 / DEGREE_DIVISIONS_CONFIG.DIVISION_SPACING;
        for (let i = 1; i <= totalDivisions; i++) {
            const degree = i * DEGREE_DIVISIONS_CONFIG.DIVISION_SPACING;
            const progress = degree / 30; // 0 to 1
            const isMajor = degree % 5 === 0; // Every 5th degree is major
            
            // Position along the line
            const pointX = lineCoords.startX + (lineCoords.endX - lineCoords.startX) * progress;
            const pointY = lineCoords.startY + (lineCoords.endY - lineCoords.startY) * progress;
            
            // Division mark length from config
            const divisionLength = isMajor ? 
                DEGREE_DIVISIONS_CONFIG.MAJOR_DIVISION_LENGTH : 
                DEGREE_DIVISIONS_CONFIG.DIVISION_LENGTH;
            
            // Calculate positioning based on config
            const halfLength = DEGREE_DIVISIONS_CONFIG.CENTER_ON_LINE ? 
                divisionLength / 2 : 
                0; // If not centered, start from line
            
            // Apply perpendicular offset from config
            const offsetFromLine = DEGREE_DIVISIONS_CONFIG.PERPENDICULAR_OFFSET;
            
            // Calculate start and end points of division mark with offset
            const centerX = pointX + perpX * offsetFromLine;
            const centerY = pointY + perpY * offsetFromLine;
            
            const startPoint: Point = {
                x: centerX - perpX * halfLength,
                y: centerY - perpY * halfLength
            };
            
            const endPoint: Point = {
                x: centerX + perpX * halfLength,
                y: centerY + perpY * halfLength
            };
            
            divisions.push({
                startPoint,
                endPoint,
                degree,
                isMajor
            });
        }
        
        return divisions;
    }

    /**
     * Calculate divisions for arc lines (corner houses 0, 3, 6, 9)
     */
    private calculateArcDegreeDivisions(
        containerIndex: number, 
        containerBounds: ContainerBounds, 
        insetRatio: number = 0
    ): DegreeDivisionMark[] {
        const divisions: DegreeDivisionMark[] = [];
        
        // Create divisions along the arc based on spacing config
        const totalDivisions = 30 / DEGREE_DIVISIONS_CONFIG.DIVISION_SPACING;
        for (let i = 1; i <= totalDivisions; i++) {
            const degree = i * DEGREE_DIVISIONS_CONFIG.DIVISION_SPACING;
            const progress = degree / 30; // 0 to 1
            const isMajor = degree % 5 === 0; // Every 5th degree is major
            
            // Get point on arc at this progress
            const arcPoint = this.getPointOnArcPath(containerIndex, progress, containerBounds, insetRatio);
            
            // Calculate tangent direction at this point for perpendicular division
            const tangentDirection = this.calculateArcTangent(containerIndex, progress, containerBounds, insetRatio);
            
            // Perpendicular direction (90 degrees rotated)
            const perpX = -tangentDirection.y;
            const perpY = tangentDirection.x;
            
            // Division mark length from config
            const divisionLength = isMajor ? 
                DEGREE_DIVISIONS_CONFIG.MAJOR_DIVISION_LENGTH : 
                DEGREE_DIVISIONS_CONFIG.DIVISION_LENGTH;
            
            // Calculate positioning based on config
            const halfLength = DEGREE_DIVISIONS_CONFIG.CENTER_ON_LINE ? 
                divisionLength / 2 : 
                0; // If not centered, start from line
            
            // Apply perpendicular offset from config
            const offsetFromLine = DEGREE_DIVISIONS_CONFIG.PERPENDICULAR_OFFSET;
            
            // Calculate start and end points of division mark with offset
            const centerX = arcPoint.x + perpX * offsetFromLine;
            const centerY = arcPoint.y + perpY * offsetFromLine;
            
            const startPoint: Point = {
                x: centerX - perpX * halfLength,
                y: centerY - perpY * halfLength
            };
            
            const endPoint: Point = {
                x: centerX + perpX * halfLength,
                y: centerY + perpY * halfLength
            };
            
            divisions.push({
                startPoint,
                endPoint,
                degree,
                isMajor
            });
        }
        
        return divisions;
    }

    /**
     * Calculate tangent direction at point on arc (for perpendicular divisions)
     */
    public calculateArcTangent(
        containerIndex: number, 
        progress: number, 
        containerBounds: ContainerBounds, 
        insetRatio: number = 0
    ): Point {
        // Get two close points on the arc to calculate tangent direction
        const epsilon = 0.001;
        const point1 = this.getPointOnArcPath(containerIndex, Math.max(0, progress - epsilon), containerBounds, insetRatio);
        const point2 = this.getPointOnArcPath(containerIndex, Math.min(1, progress + epsilon), containerBounds, insetRatio);
        
        // Calculate tangent vector
        const tangentX = point2.x - point1.x;
        const tangentY = point2.y - point1.y;
        
        // Normalize to unit vector
        const length = Math.sqrt(tangentX * tangentX + tangentY * tangentY);
        
        return {
            x: tangentX / length,
            y: tangentY / length
        };
    }

    // ===== НОВЫЕ МЕТОДЫ ДЛЯ CALCULATIONSPROVIDER =====

    /**
     * Рассчитать координаты линии градусов для контейнера (новый интерфейс для CalculationsProvider)
     */
    public calculateDegreeLineCoordinatesForBounds(
        containerBounds: ContainerBounds, 
        insetRatio: number = 0
    ): LineCoordinates {
        // Определяем индекс контейнера из bounds (если доступен)
        const containerIndex = containerBounds.index || 0;
        return this.calculateDegreeLineCoordinates(containerIndex, containerBounds, insetRatio);
    }

    /**
     * Рассчитать метки делений градусов (новый интерфейс для CalculationsProvider)
     */
    public calculateDegreeDivisionMarks(
        containerBounds: ContainerBounds,
        insetRatio: number = 0
    ): DegreeDivisionMark[] {
        // Определяем индекс контейнера из bounds (если доступен)
        const containerIndex = containerBounds.index || 0;
        return this.calculateDegreeDivisions(containerIndex, containerBounds, insetRatio);
    }

    /**
     * Рассчитать позицию планеты на линии градусов
     */
    public calculatePlanetPositionOnLine(
        planet: any, // GrahaPosition
        containerBounds: ContainerBounds,
        insetRatio: number = 0
    ): Point {
        const containerIndex = containerBounds.index || 0;
        
        // Получаем координаты линии градусов
        const lineCoords = this.calculateDegreeLineCoordinates(containerIndex, containerBounds, insetRatio);
        
        // Вычисляем прогресс планеты по градусам из position координат
        // GrahaPosition имеет position: Coordinate с degrees, minutes, seconds
        let degreeInSign = 0;
        if (planet.position && typeof planet.position.degrees === 'number') {
            // Полные градусы + минуты + секунды в десятичном виде
            const totalDegrees = planet.position.degrees + (planet.position.minutes || 0) / 60 + (planet.position.seconds || 0) / 3600;
            degreeInSign = totalDegrees % 30; // Градусы в знаке (0-30)
        } else if (planet.longitude) {
            // Fallback если есть longitude
            degreeInSign = planet.longitude % 30;
        }
        
        const progress = degreeInSign / 30; // 0 to 1
        
        // Позиция на линии
        const planetX = lineCoords.startX + (lineCoords.endX - lineCoords.startX) * progress;
        const planetY = lineCoords.startY + (lineCoords.endY - lineCoords.startY) * progress;
        
        return { x: planetX, y: planetY };
    }

    /**
     * Рассчитать все линии градусов для множества контейнеров
     */
    public calculateAllContainerLines(
        containerBoundsMapping: Map<string, ContainerBounds>,
        insetRatio: number = 0
    ): Map<string, LineCoordinates> {
        const result = new Map<string, LineCoordinates>();
        
        containerBoundsMapping.forEach((bounds, containerId) => {
            try {
                const lineCoords = this.calculateDegreeLineCoordinatesForBounds(bounds, insetRatio);
                result.set(containerId, lineCoords);
            } catch (error) {
                devWarn(`Failed to calculate line for container ${containerId}:`, error);
            }
        });
        
        return result;
    }

    /**
     * Рассчитать все метки делений для множества контейнеров
     */
    public calculateAllContainerDivisions(
        containerBoundsMapping: Map<string, ContainerBounds>,
        insetRatio: number = 0
    ): Map<string, DegreeDivisionMark[]> {
        const result = new Map<string, DegreeDivisionMark[]>();
        
        containerBoundsMapping.forEach((bounds, containerId) => {
            try {
                const divisions = this.calculateDegreeDivisionMarks(bounds, insetRatio);
                result.set(containerId, divisions);
            } catch (error) {
                devWarn(`Failed to calculate divisions for container ${containerId}:`, error);
            }
        });
        
        return result;
    }
}